import { times } from 'lodash'

import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import SegmentedControl from '../app/design/SegmentedControl'
import { CurrencyField } from '../common/fields/CurrencyField'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { discountOptions as rawDiscountOptions } from '../settings/Options'
import { WorkflowIconSection } from './WorkflowIconSection'

type Props = {
    isOpen: boolean
    onClose: () => void
    discountType: 'percent' | 'rate'
    discountAmount: number
    onChange?: (newType: 'percent' | 'rate', newAmount: number) => void
    discountInstances?: {
        selectedID: string
        onUpdateID: (newID: string) => void
    }
}

export const DiscountModal = ({
    isOpen,
    onClose,
    discountType,
    discountAmount,
    onChange,
    discountInstances,
}: Props) => {
    const discountOptions = rawDiscountOptions.map(({ value, label }) => ({
        id: value,
        title: label,
    }))
    const discountInstancesItems = [
        { id: '', title: 'Continuous' },
        ...times(52, (index) => ({
            id: (index + 1).toString(),
            title: (index + 1).toString(),
        })),
    ]

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Discount"
            footer={
                <div className="flex justify-end">
                    <Button intent="primary" onClick={onClose}>
                        Done
                    </Button>
                </div>
            }
            width={744}
        >
            <div className="flex flex-col items-stretch justify-start gap-y-8">
                <WorkflowIconSection title="Discount" icon="discount.large" className="z-20">
                    <div className="flex items-center justify-between gap-x-2">
                        <SegmentedControl
                            segments={[
                                { label: 'Percent of total', value: 'percent' },
                                { label: 'Fixed', value: 'rate' },
                            ]}
                            value={discountType}
                            onChange={(newDiscountType) =>
                                onChange?.(newDiscountType as Props['discountType'], 0)
                            }
                            className="w-[290px] shrink-0"
                        />

                        {discountType === 'percent' ? (
                            <SearchableSelect
                                size="medium"
                                items={discountOptions}
                                selectedItemID={discountAmount.toString()}
                                onSelectItem={(option) =>
                                    onChange?.(discountType, parseInt(option?.id ?? '0'))
                                }
                                className="grow"
                                shouldPreserveLastSelection
                                shouldPortal={false}
                            />
                        ) : (
                            <CurrencyField
                                value={discountAmount}
                                onChange={(newValue) => onChange?.(discountType, newValue ?? 0)}
                                size="medium"
                                className="grow"
                                isFullWidth
                            />
                        )}
                    </div>
                </WorkflowIconSection>

                {discountInstances !== undefined && (
                    <WorkflowIconSection
                        title="Number of discounts"
                        icon="hash.large"
                        description="Select the number of discounts"
                    >
                        <SearchableSelect
                            items={discountInstancesItems}
                            onSelectItem={(item) => discountInstances.onUpdateID(item?.id ?? '')}
                            selectedItemID={discountInstances.selectedID}
                            size="medium"
                            shouldPreserveLastSelection
                        />
                    </WorkflowIconSection>
                )}
            </div>
        </Modal>
    )
}
